'use client'

import { useState } from 'react'
import { RichText } from '@/components/ui'
import type { TeaserImageWithContentAccordianItemType } from '@/lib/types/teaser'

type Props = {
  items: TeaserImageWithContentAccordianItemType[]
  className?: string
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="9"
      viewBox="0 0 16 9"
      fill="none"
      aria-hidden="true"
      className={`shrink-0 transition-transform duration-300 ease-in-out ${
        open ? 'rotate-180' : 'rotate-0'
      }`}
    >
      <path
        d="M14.9336 0.933105L7.93359 7.93311L0.933594 0.933105"
        stroke="currentColor"
        strokeWidth="1.86667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function TeaserImageWithContentAccordion({
  items,
  className = '',
}: Props) {
  const [openId, setOpenId] = useState<number | null>(null)
  const entries = items.filter((item) => item?.heading)

  if (!entries.length) return null

  return (
    <div
      className={`flex w-full flex-col ${className}`.trim()}
      data-fig="teaser-iwc-accordion"
    >
      {entries.map((item) => {
        const isOpen = openId === item.id
        const headingId = `teaser-iwc-acc-h-${item.id}`
        const panelId = `teaser-iwc-acc-p-${item.id}`
        const hasBody =
          Array.isArray(item.description) && item.description.length > 0

        return (
          <div
            key={item.id}
            className="w-full border-b border-brand-primary/40 pb-2"
          >
            <button
              type="button"
              id={headingId}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() =>
                setOpenId((prev) => (prev === item.id ? null : item.id))
              }
              className="flex w-full cursor-pointer items-center justify-between gap-4 py-3 text-left font-body text-[15px] font-normal leading-[150%] text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-dark md:text-lg md:leading-7"
            >
              <span className="min-w-0 flex-1 pr-2">{item.heading}</span>
              <ChevronIcon open={isOpen} />
            </button>

            <section
              id={panelId}
              aria-labelledby={headingId}
              className="grid transition-[grid-template-rows] duration-300 ease-in-out"
              style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
              aria-hidden={!isOpen}
              inert={!isOpen ? true : undefined}
            >
              <div className="min-h-0 overflow-hidden">
                {hasBody ? (
                  <div
                    className={`pb-3 pt-1 transition-opacity duration-300 ${
                      isOpen ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <RichText
                      content={item.description}
                      className="flex w-full flex-col gap-2 font-body text-sm font-normal leading-[150%] text-muted md:text-base md:leading-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-1"
                    />
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        )
      })}
    </div>
  )
}
